import React, { useState, useEffect } from "react";
import emailjs from "emailjs-com";
import { countriesList } from "../utility/constants";
import { X, Send, AlertCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FortisContactModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [country, setCountry] = useState("");
  const [countryError, setCountryError] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);

  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && open) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscKey);
    
    // Lock body scroll when modal is open
    if (open) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "auto";
    };
  }, [open, onClose]);

  const resetForm = () => {
    setName("");
    setNameError("");
    setEmail("");
    setEmailError("");
    setPhone("");
    setPhoneError("");
    setCountry("");
    setCountryError("");
    setMessage("");
    setMessageError("");
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Allow numbers, plus sign, hyphens, and spaces
    const validCharacters = value.replace(/[^0-9+\-\s]/g, '');
    setPhone(validCharacters);
    
    if (validCharacters.trim() === "") {
      setPhoneError("Phone number is required");
    } else {
      setPhoneError("");
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    
    if (value.trim() === "") {
      setNameError("Name is required");
    } else if (value.length < 2) {
      setNameError("Name must be at least 2 characters");
    } else {
      setNameError("");
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value.trim() === "") {
      setEmailError("Email is required");
    } else if (!emailRegex.test(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;
    setCountry(value);
    
    if (value === "") {
      setCountryError("Please select your country");
    } else {
      setCountryError("");
    }
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    
    if (value.trim() === "") {
      setMessageError("Message is required");
    } else if (value.length < 10) {
      setMessageError("Message should be at least 10 characters");
    } else {
      setMessageError("");
    }
  };

  const validateForm = () => {
    let isValid = true;
    
    // Validate name
    if (name.trim() === "") {
      setNameError("Name is required");
      isValid = false;
    } else if (name.length < 2) {
      setNameError("Name must be at least 2 characters");
      isValid = false;
    }
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email.trim() === "") {
      setEmailError("Email is required");
      isValid = false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      isValid = false;
    }
    
    // Validate phone
    if (phone.trim() === "") {
      setPhoneError("Phone number is required");
      isValid = false;
    }
    
    // Validate country
    if (!country) {
      setCountryError("Please select your country");
      isValid = false;
    }
    
    // Validate message
    if (message.trim() === "") {
      setMessageError("Message is required");
      isValid = false;
    } else if (message.length < 10) {
      setMessageError("Message should be at least 10 characters");
      isValid = false;
    }
    
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    const serviceid = "service_7yxtqsn";
    const templateid = "template_x3wusd7";
    const publickey = "4UpyJJj3P5JGeMZFz";

    const messageContent = `
      Name: ${name}
      Email: ${email}
      Phone: ${phone}
      Country: ${country}
      Query: ${message}
    `;

    const templateparams = {
      from_name: name,
      from_email: email,
      to_name: "web",
      message: messageContent,
    };

    emailjs
      .send(serviceid, templateid, templateparams, publickey)
      .then(() => {
        setLoading(false);
        setSuccessMessage(true);
        resetForm();
        setTimeout(() => {
          onClose();
          navigate('/hospitals/fortis/thank-you');
        }, 1000);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Email sending error:", error);
      });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div 
        className="bg-white rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-auto relative animate-[fadeIn_0.3s_ease-out]"
        onClick={(e) => e.stopPropagation()}
        style={{animation: "0.3s ease-out 0s 1 normal none running fadeIn"}}
      >
        <div className="bg-green-600 text-white py-3 px-6 relative rounded-t-xl flex items-center justify-center">
          <h2 className="text-xl font-semibold m-0 text-center">Contact Us</h2>
          <button 
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none text-white w-7 h-7 flex items-center justify-center cursor-pointer rounded-full hover:bg-white hover:bg-opacity-20"
            onClick={onClose} 
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        
        <div className=" p-3 bg-gray-50">
          <p className="text-center text-gray-500 text-sm mt-0 mb-3">
            Fill out the form and we'll get back to you shortly.
          </p>
          
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col">
              <label htmlFor="name" className="mb-1 text-sm font-medium text-gray-600">Name*</label>
              <input
                id="name"
                type="text"
                className={`px-3 py-2 h-9 border ${nameError ? 'border-red-500 bg-red-50' : 'border-gray-200'} rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 focus:border-green-500 transition`}
                value={name}
                onChange={handleNameChange}
                placeholder="Your full name"
                required
              />
              {nameError && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle size={12} />
                  <span>{nameError}</span>
                </div>
              )}
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="email" className="mb-1 text-sm font-medium text-gray-600">Email*</label>
              <input
                id="email"
                type="email"
                className={`px-3 py-2 h-9 border ${emailError ? 'border-red-500 bg-red-50' : 'border-gray-200'} rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 focus:border-green-500 transition`}
                value={email}
                onChange={handleEmailChange}
                placeholder="Your email address"
                required
              />
              {emailError && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle size={12} />
                  <span>{emailError}</span>
                </div>
              )}
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="phone" className="mb-1 text-sm font-medium text-gray-600">Phone*</label>
              <input
                id="phone"
                type="tel"
                className={`px-3 py-2 h-9 border ${phoneError ? 'border-red-500 bg-red-50' : 'border-gray-200'} rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 focus:border-green-500 transition`}
                value={phone}
                onChange={handlePhoneChange}
                placeholder="+91 xxxxx xxxxx (with country code)"
                required
              />
              {phoneError && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle size={12} />
                  <span>{phoneError}</span>
                </div>
              )}
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="country" className="mb-1 text-sm font-medium text-gray-600">Country*</label>
              <select
                id="country"
                className={`px-3 py-2 h-9 border ${countryError ? 'border-red-500 bg-red-50' : 'border-gray-200'} rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 focus:border-green-500 transition`}
                value={country}
                onChange={handleCountryChange}
                required
              >
                <option value="">Select your country</option>
                {countriesList.map((item, index) => (
                  <option value={item.name} key={`${item.name}-${index}`}>
                    {item.name}
                  </option>
                ))}
              </select>
              {countryError && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle size={12} />
                  <span>{countryError}</span>
                </div>
              )}
            </div>
            
            <div className="flex flex-col">
              <label htmlFor="message" className="mb-1 text-sm font-medium text-gray-600">Your Query*</label>
              <textarea
                id="message"
                className={`px-3 py-2 border ${messageError ? 'border-red-500 bg-red-50' : 'border-gray-200'} rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 focus:border-green-500 transition resize-y min-h-[90px]`}
                value={message}
                onChange={handleMessageChange}
                rows={3}
                placeholder="How can we help you?"
                required
              ></textarea>
              {messageError && (
                <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
                  <AlertCircle size={12} />
                  <span>{messageError}</span>
                </div>
              )}
            </div>
            
            <button 
              type="submit" 
              className="flex items-center justify-center gap-2 mt-2 bg-green-600 text-white py-2 px-5 border-none rounded-md text-base font-semibold cursor-pointer transition duration-200 ease-in-out hover:bg-green-700 hover:-translate-y-1 active:translate-y-0 disabled:cursor-not-allowed self-center disabled:hover:translate-y-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              disabled={loading}
            >
              {loading ? (
              <Loader2 className=" animate-spin" />
              ) : (
                <>
                  <Send size={16} />
                  <span>Submit</span>
                </>
              )}
            </button>
          </form>
        </div>
        
        {successMessage && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-green-600 text-white py-2 px-5 rounded shadow-lg z-10 font-medium text-sm animate-[messageAppear_0.3s_ease-out]">
            Message sent successfully!
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes messageAppear {
          from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default FortisContactModal;